import React, { useEffect, useState } from 'react'
import { formatDate } from '@/common/time'
import { MachineName } from '@/components/machine'
import { Order, OrderManager } from '@/components/order/Order'
import { StaticPaginatedTable, StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'
import { SearchCriteria, SearchCriteriaParams } from '@/pages/communal/statistics/components/common/searchCriteria'
import { MachineType } from '@/services/model/machine'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'

interface CountHitPopupProps {
  services: Services
  session: CurrentSession
  criteria: SearchCriteria
  bucketFor: Date
  goToMachinePage: (machineId: string) => void
  onClose: () => void
}

interface MachineHitCount {
  imei: string
  countHits: number
  machine: {
    id: string
    imei: string
    internalNumber: string
    type: MachineType
  }
}

const CountHitPopupOrderCols = ['machine.internalNumber', 'countHits']
const CountHitPopupDefaultOrder: Order = {
  key: 'countHits',
  asc: false
}

export const CountHitPopupBody = ({ services, session, criteria, bucketFor, goToMachinePage, onClose }: CountHitPopupProps) => {
  const bucketForTime = bucketFor.getTime()
  const [response, setResponse] = useState<MachineHitCount[]>()
  const [listState, setListState] = useState<StaticPaginatedTableState>({ pageNumber: 1, pageSize: 10 })
  const [orderManager, setOrderManager] = useState<OrderManager>(() => new OrderManager(
    CountHitPopupOrderCols,
    CountHitPopupDefaultOrder,
    (order: Order) => {
      setOrderManager((current) => current.newManager(order))
      setListState((current) => ({ ...current, pageNumber: 1 }))
    }
  ))

  useEffect(() => {
    setResponse(undefined)
    services.session
      .fetchWithToken(`/api/communal/statistics/machines/bucket/hitcounts?bucketFor=${new Date(bucketForTime).toISOString()}&${SearchCriteriaParams(criteria)}`)
      .then((r) => r.json())
      .then(setResponse)
  }, [services.session, criteria, bucketForTime])

  if (!response) {
    return <div className='spinner'></div>
  }

  const headerRow = (key: string, translationKey: string) => <div className='hcol' onClick={() => orderManager.click(key)}>
    {session.translates.for(translationKey)}
    {orderManager.orderInfoElement(key)}
  </div>

  return <div>
    <h3>{formatDate(bucketFor)}</h3>
    <StaticPaginatedTable<MachineHitCount>
      data={orderManager.orderListByKey(response)}
      parentState={listState}
      setParentState={setListState}
      header={() => <>
        {headerRow('machine.internalNumber', 'elements.table.maxhitpop.machine')}
        {headerRow('countHits', 'elements.table.sessions.total_hits')}
      </>}
      row={(row) => <>
        <div className='col' onClick={() => {
          goToMachinePage(row.machine.id)
          onClose()
        }}><MachineName row={row.machine} /></div>
        <div className='col'>{row.countHits}</div>
      </>}
      translates={session.translates}
    />
  </div>
}
